import { httpClient } from './http-client';
import { API_ENDPOINTS } from '../config/api';

export type AuctionType = 'Offline' | 'Online' | 'Hybrid';
export type AuctionStatus = 'Upcoming' | 'Active' | 'Completed';

export interface AuctionLotItem {
  id: number;
  auctionId: number;
  lotNumber: number;
  name: string;
  inj: string;
  birthDate?: string | null;
  ageMonths?: number | null;
  birthWeight?: number | null;
  weaningWeight?: number | null;
  yearlingWeight?: number | null;
  currentWeight?: number | null;
  averageDailyGain?: number | null;
  scrotalCircumference?: number | null;
  spermQualityScore?: number | null;
  heightAtWithers?: number | null;
  line?: string | null;
  startingPrice: number;
  image?: string | null;
  imageUrl?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface AuctionListItem {
  id: number;
  titleKz: string;
  titleRu: string;
  farmNameKz: string;
  farmNameRu: string;
  regionKz: string;
  regionRu: string;
  date: string;
  auctionType: AuctionType;
  breed: string;
  status: AuctionStatus;
  coverImage?: string | null;
  lotsCount: number;
}

export interface AuctionItem {
  id: number;
  titleKz: string;
  titleRu: string;
  descriptionKz?: string | null;
  descriptionRu?: string | null;
  farmNameKz: string;
  farmNameRu: string;
  regionKz: string;
  regionRu: string;
  date: string;
  auctionType: AuctionType;
  breed: string;
  status: AuctionStatus;
  youtubeUrl?: string | null;
  youtubeEmbedUrl?: string | null;
  coverImage?: string | null;
  bannerImage?: string | null;
  lots: AuctionLotItem[];
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateAuctionRequest {
  titleKz: string;
  titleRu: string;
  descriptionKz?: string | null;
  descriptionRu?: string | null;
  farmNameKz: string;
  farmNameRu: string;
  regionKz: string;
  regionRu: string;
  date: string;
  auctionType: AuctionType;
  breed: string;
  status: AuctionStatus;
  youtubeUrl?: string | null;
  youtubeEmbedUrl?: string | null;
  coverImage?: string | null;
  bannerImage?: string | null;
}

export interface CreateAuctionLotRequest {
  lotNumber: number;
  name: string;
  inj: string;
  birthDate?: string | null;
  ageMonths?: number | null;
  birthWeight?: number | null;
  weaningWeight?: number | null;
  yearlingWeight?: number | null;
  currentWeight?: number | null;
  averageDailyGain?: number | null;
  scrotalCircumference?: number | null;
  spermQualityScore?: number | null;
  heightAtWithers?: number | null;
  line?: string | null;
  startingPrice: number;
  image?: string | null;
  imageUrl?: string | null;
}

const BASE = API_ENDPOINTS.AUCTION;

export const auctionService = {
  getAll(status?: AuctionStatus, auctionType?: AuctionType, search?: string) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (auctionType) params.set('auctionType', auctionType);
    if (search) params.set('search', search);
    const qs = params.toString();
    return httpClient.get<AuctionListItem[]>(BASE + (qs ? `?${qs}` : ''));
  },

  getById(id: number) {
    return httpClient.get<AuctionItem>(`${BASE}/${id}`);
  },

  create(body: CreateAuctionRequest) {
    return httpClient.post<AuctionItem>(BASE, body);
  },

  update(id: number, body: CreateAuctionRequest) {
    return httpClient.put<AuctionItem>(`${BASE}/${id}`, body);
  },

  delete(id: number) {
    return httpClient.delete(`${BASE}/${id}`);
  },

  createLot(auctionId: number, body: CreateAuctionLotRequest) {
    return httpClient.post<AuctionLotItem>(`${BASE}/${auctionId}/lots`, body);
  },

  updateLot(lotId: number, body: CreateAuctionLotRequest) {
    return httpClient.put<AuctionLotItem>(`${BASE}/lots/${lotId}`, body);
  },

  deleteLot(lotId: number) {
    return httpClient.delete(`${BASE}/lots/${lotId}`);
  },
};
